import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// Account roles, resolved from the signed session token minted by
// /api/auth/login against the worklog_users table.
//   'owner'     - role=admin (Keith): every surface
//   'user'      - role=user  (Jenny): the dashboard, not owner-only surfaces
//   'anonymous' - not signed in: the sign-in screen, nothing else
export type Role = 'owner' | 'user' | 'anonymous';

export interface SessionUser {
  email: string;
  name: string;
  role: 'admin' | 'user';
}

interface AuthState {
  role: Role;
  user: SessionUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string, registerToken: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

// The session is an httpOnly cookie the server sets; JS cannot read it, so
// there is no client token store to drift. /api/auth/me is asked on every load
// and is the only source of truth for "who am I". Deliberately nothing is
// cached: the old code optimistically restored a role from sessionStorage,
// which is the same class of bug that let this app serve a month-old snapshot
// as if it were live. A stale identity is worse than a spinner.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!r.ok) { setUser(null); return; }
      const data = await r.json() as { user?: SessionUser };
      setUser(data.user ?? null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // On success the server has set the httpOnly session cookie; the CALLER then
  // navigates (window.location.assign) rather than us flipping React state here.
  // A real navigation is the lifecycle password managers expect for save-on-
  // submit, and it forces a fresh /api/auth/me + snapshot from the server - the
  // safer default for an app with a history of serving stale client state. So we
  // deliberately do NOT setUser: the reload rebuilds identity from the server.
  const signIn = useCallback(async (email: string, password: string) => {
    const r = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json().catch(() => ({})) as { error?: string };
    if (!r.ok) throw new Error(data.error || `Sign in failed (HTTP ${r.status})`);
  }, []);

  // Create an account. The server only accepts addresses already named in
  // ADMIN_EMAILS/APPROVED_EMAILS and signs you straight in on success - there is
  // no approval queue to wait in.
  const signUp = useCallback(async (name: string, email: string, password: string, registerToken: string) => {
    const r = await fetch('/api/auth/register', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, email, password, registerToken }),
    });
    const data = await r.json().catch(() => ({})) as { error?: string };
    if (!r.ok) throw new Error(data.error || `Create account failed (HTTP ${r.status})`);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } finally {
      setUser(null);
    }
  }, []);

  const role: Role = !user ? 'anonymous' : (user.role === 'admin' ? 'owner' : 'user');

  return (
    <AuthCtx.Provider value={{ role, user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside <AuthProvider>');
  return v;
}

export function useIsOwner() {
  return useAuth().role === 'owner';
}
